import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { generatePromoCode } from "@/utils/generate-promo-code";
import { AdminUserDTO } from "@shaarp/common";
import { InfoIcon, Loader2Icon, SparklesIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast, useToastError } from "../ui/use-toast";

type PromoMode = "existing" | "create";

export function DialogAdminUserReferral({
  user,
  open,
  onClose,
  onSuccess,
}: {
  user: AdminUserDTO;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) {
  const { toast } = useToast();
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();

  // Unified mode: use same code for both monthly and annual
  const [useSameCode, setUseSameCode] = useState(false);

  // Unified fields (when useSameCode is true)
  const [unifiedMode, setUnifiedMode] = useState<PromoMode>("create");
  const [unifiedPromoCode, setUnifiedPromoCode] = useState("");
  const [unifiedDiscount, setUnifiedDiscount] = useState("");
  const [unifiedDuration, setUnifiedDuration] = useState<string>("3");

  // Monthly fields
  const [monthlyMode, setMonthlyMode] = useState<PromoMode>("create");
  const [monthlyPromoCode, setMonthlyPromoCode] = useState("");
  const [monthlyDiscount, setMonthlyDiscount] = useState("");
  const [monthlyDuration, setMonthlyDuration] = useState<string>("3");

  // Annual fields
  const [annualMode, setAnnualMode] = useState<PromoMode>("create");
  const [annualPromoCode, setAnnualPromoCode] = useState("");
  const [annualDiscount, setAnnualDiscount] = useState("");
  const [annualDuration, setAnnualDuration] = useState<string>("3");

  const [isLoading, setIsLoading] = useState(false);

  const hasExistingReferral =
    user.referralMonthlyDiscount !== null ||
    user.referralAnnualDiscount !== null;

  const initializeForm = () => {
    if (hasExistingReferral) {
      // Check if same code for both
      const sameCode =
        user.referralMonthlyPromotionCode ===
          user.referralAnnualPromotionCode &&
        user.referralMonthlyPromotionCode !== null;
      setUseSameCode(sameCode);

      if (sameCode) {
        setUnifiedPromoCode(user.referralMonthlyPromotionCode || "");
        setUnifiedDiscount(user.referralMonthlyDiscount || "");
        setUnifiedMode("existing"); // Existing code by default when editing
      } else {
        // Monthly
        if (user.referralMonthlyPromotionCode) {
          setMonthlyPromoCode(user.referralMonthlyPromotionCode);
          setMonthlyDiscount(user.referralMonthlyDiscount || "");
          setMonthlyMode("existing"); // Existing code by default when editing
        }

        // Annual
        if (user.referralAnnualPromotionCode) {
          setAnnualPromoCode(user.referralAnnualPromotionCode);
          setAnnualDiscount(user.referralAnnualDiscount || "");
          setAnnualMode("existing"); // Existing code by default when editing
        }
      }
    } else {
      setUseSameCode(false);
      setUnifiedPromoCode("");
      setUnifiedDiscount("");
      setUnifiedDuration("3");
      setUnifiedMode("create");
      setMonthlyPromoCode("");
      setMonthlyDiscount("");
      setMonthlyDuration("3");
      setMonthlyMode("create");
      setAnnualPromoCode("");
      setAnnualDiscount("");
      setAnnualDuration("3");
      setAnnualMode("create");
    }
  };

  useEffect(() => {
    if (open) {
      initializeForm();
    }
  }, [
    open,
    user.referralMonthlyDiscount,
    user.referralAnnualDiscount,
    user.referralMonthlyPromotionCode,
    user.referralAnnualPromotionCode,
  ]);

  const handleGenerateCode = (target: "unified" | "monthly" | "annual") => {
    const code = generatePromoCode(user.firstName, user.lastName, user.email);
    if (target === "unified") {
      setUnifiedPromoCode(code);
    } else if (target === "monthly") {
      setMonthlyPromoCode(code);
    } else {
      setAnnualPromoCode(code);
    }
  };

  const validateForm = (): { valid: boolean; error?: string } => {
    if (useSameCode) {
      // Unified validation
      if (unifiedPromoCode || unifiedDiscount) {
        if (!unifiedPromoCode || !unifiedPromoCode.trim()) {
          return {
            valid: false,
            error: t(
              "admin.dataTableAdminUsers.dialog.addReferral.errorPromoCodeRequired"
            ),
          };
        }
        if (unifiedMode === "create") {
          if (!unifiedDiscount || !unifiedDiscount.trim()) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorDiscountRequired"
              ),
            };
          }
          const discount = parseFloat(unifiedDiscount);
          if (isNaN(discount) || discount <= 0 || discount > 100) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorDiscountInvalid"
              ),
            };
          }
        }
      }
    } else {
      // Separate validation
      // Monthly
      if (monthlyPromoCode || monthlyDiscount) {
        if (!monthlyPromoCode || !monthlyPromoCode.trim()) {
          return {
            valid: false,
            error: t(
              "admin.dataTableAdminUsers.dialog.addReferral.errorMonthlyPromoCodeRequired"
            ),
          };
        }
        if (monthlyMode === "create") {
          if (!monthlyDiscount || !monthlyDiscount.trim()) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorMonthlyDiscountRequired"
              ),
            };
          }
          const discount = parseFloat(monthlyDiscount);
          if (isNaN(discount) || discount <= 0 || discount > 100) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorDiscountInvalid"
              ),
            };
          }
        }
      }

      // Annual
      if (annualPromoCode || annualDiscount) {
        if (!annualPromoCode || !annualPromoCode.trim()) {
          return {
            valid: false,
            error: t(
              "admin.dataTableAdminUsers.dialog.addReferral.errorAnnualPromoCodeRequired"
            ),
          };
        }
        if (annualMode === "create") {
          if (!annualDiscount || !annualDiscount.trim()) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorAnnualDiscountRequired"
              ),
            };
          }
          const discount = parseFloat(annualDiscount);
          if (isNaN(discount) || discount <= 0 || discount > 100) {
            return {
              valid: false,
              error: t(
                "admin.dataTableAdminUsers.dialog.addReferral.errorDiscountInvalid"
              ),
            };
          }
        }
      }

      // Check if at least one promo is defined
      if (
        !monthlyPromoCode &&
        !monthlyDiscount &&
        !annualPromoCode &&
        !annualDiscount
      ) {
        return {
          valid: false,
          error: t(
            "admin.dataTableAdminUsers.dialog.addReferral.errorAtLeastOne"
          ),
        };
      }
    }

    return { valid: true };
  };

  const handleSubmit = async () => {
    // Validate form
    const validation = validateForm();
    if (!validation.valid) {
      toast({
        variant: "destructive",
        title: t("admin.dataTableAdminUsers.toast.validationError"),
        description: validation.error,
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload: {
        monthly?: {
          promotionCode: string | null;
          discount: string | null;
          durationInMonths?: number | null;
        } | null;
        annual?: {
          promotionCode: string | null;
          discount: string | null;
          durationInMonths?: number | null;
        } | null;
      } = {};

      if (useSameCode) {
        // Use same code for both
        if (unifiedPromoCode || unifiedDiscount) {
          if (unifiedMode === "existing") {
            // Use existing code
            payload.monthly = {
              promotionCode: unifiedPromoCode.trim(),
              discount: null,
              durationInMonths: null,
            };
            payload.annual = {
              promotionCode: unifiedPromoCode.trim(),
              discount: null,
              durationInMonths: null,
            };
          } else {
            // Create new code - unified mode creates a single 'once' code
            // This code will apply once to both monthly and annual subscriptions
            payload.monthly = {
              promotionCode: unifiedPromoCode.trim(),
              discount: unifiedDiscount.trim(),
              durationInMonths: null, // 'once' for unified mode
            };
            payload.annual = {
              promotionCode: unifiedPromoCode.trim(),
              discount: unifiedDiscount.trim(),
              durationInMonths: null, // 'once' for unified mode
            };
          }
        } else {
          // Clear both when unified mode has no data
          payload.monthly = null;
          payload.annual = null;
        }
      } else {
        // Separate codes
        // Monthly
        if (monthlyPromoCode || monthlyDiscount) {
          const durationInMonths =
            monthlyDuration === "unlimited" ? null : parseInt(monthlyDuration);

          if (monthlyMode === "existing") {
            payload.monthly = {
              promotionCode: monthlyPromoCode.trim(),
              discount: null,
              durationInMonths: null,
            };
          } else {
            payload.monthly = {
              promotionCode: monthlyPromoCode.trim(),
              discount: monthlyDiscount.trim(),
              durationInMonths,
            };
          }
        }
        // If monthly is empty, don't include it (undefined = don't update)
        // Only explicitly set to null if we want to clear it (which should be handled by the delete endpoint)

        // Annual
        if (annualPromoCode || annualDiscount) {
          const durationInMonths =
            annualDuration === "unlimited" ? null : parseInt(annualDuration);

          if (annualMode === "existing") {
            payload.annual = {
              promotionCode: annualPromoCode.trim(),
              discount: null,
              durationInMonths: null,
            };
          } else {
            payload.annual = {
              promotionCode: annualPromoCode.trim(),
              discount: annualDiscount.trim(),
              durationInMonths,
            };
          }
        }
        // If annual is empty, don't include it (undefined = don't update)
      }

      await api.post(`/admin/user/${user.id}/referral`, payload);

      toast({
        variant: "default",
        title: hasExistingReferral
          ? t("admin.dataTableAdminUsers.toast.referralUpdated")
          : t("admin.dataTableAdminUsers.toast.referralAdded"),
        description: hasExistingReferral
          ? t("admin.dataTableAdminUsers.toast.referralUpdatedDescription")
          : t("admin.dataTableAdminUsers.toast.referralAddedDescription"),
      });
      onClose();
      onSuccess?.();
    } catch (e) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const renderPromoSection = (
    mode: PromoMode,
    setMode: (mode: PromoMode) => void,
    promoCode: string,
    setPromoCode: (code: string) => void,
    discount: string,
    setDiscount: (discount: string) => void,
    duration: string,
    setDuration: (duration: string) => void,
    target: "unified" | "monthly" | "annual",
    title: string
  ) => (
    <div className="space-y-3">
      <Label className="text-base font-semibold">{title}</Label>
      <RadioGroup
        value={mode}
        onValueChange={(value) => setMode(value as PromoMode)}
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="create" id={`${target}-create`} />
          <Label
            htmlFor={`${target}-create`}
            className="font-normal cursor-pointer"
          >
            {t("admin.dataTableAdminUsers.dialog.addReferral.createNew")}
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="existing" id={`${target}-existing`} />
          <Label
            htmlFor={`${target}-existing`}
            className="font-normal cursor-pointer"
          >
            {t("admin.dataTableAdminUsers.dialog.addReferral.useExisting")}
          </Label>
        </div>
      </RadioGroup>
      <div className="grid gap-3 pl-6">
        <div className="flex gap-2 items-center">
          <Label htmlFor={`${target}-promoCode`} className="w-28 text-sm">
            {t("admin.dataTableAdminUsers.dialog.addReferral.promoCode")}
          </Label>
          <div className="flex flex-1 gap-2">
            <Input
              id={`${target}-promoCode`}
              type="text"
              placeholder="PROMO2025"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              className="flex-1"
            />
            {mode === "create" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleGenerateCode(target)}
                className="gap-1"
              >
                <SparklesIcon className="w-4 h-4" />
                {t("admin.dataTableAdminUsers.dialog.addReferral.generate")}
              </Button>
            )}
          </div>
        </div>
        {mode === "create" && (
          <>
            <div className="flex gap-2 items-center">
              <Label htmlFor={`${target}-discount`} className="w-28 text-sm">
                {t("admin.dataTableAdminUsers.dialog.addReferral.discount")} (%)
              </Label>
              <Input
                id={`${target}-discount`}
                type="number"
                min="0"
                max="100"
                placeholder="10"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="w-24"
              />
            </div>
            {target === "monthly" && (
              <div className="flex gap-2 items-center">
                <Label htmlFor={`${target}-duration`} className="w-28 text-sm">
                  {t("admin.dataTableAdminUsers.dialog.addReferral.duration")}
                </Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">
                      {t(
                        "admin.dataTableAdminUsers.dialog.addReferral.months",
                        {
                          count: 3,
                        }
                      )}
                    </SelectItem>
                    <SelectItem value="6">
                      {t(
                        "admin.dataTableAdminUsers.dialog.addReferral.months",
                        {
                          count: 6,
                        }
                      )}
                    </SelectItem>
                    <SelectItem value="12">
                      {t(
                        "admin.dataTableAdminUsers.dialog.addReferral.months",
                        {
                          count: 12,
                        }
                      )}
                    </SelectItem>
                    <SelectItem value="unlimited">
                      {t(
                        "admin.dataTableAdminUsers.dialog.addReferral.unlimited"
                      )}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {target === "annual" && (
              <p className="text-xs italic text-muted-foreground">
                {t("admin.dataTableAdminUsers.dialog.addReferral.annualOnce")}
              </p>
            )}
            {target === "unified" && (
              <p className="text-xs italic text-muted-foreground">
                {t("admin.dataTableAdminUsers.dialog.addReferral.unifiedOnce")}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {hasExistingReferral
              ? t("admin.dataTableAdminUsers.dialog.editReferral.title")
              : t("admin.dataTableAdminUsers.dialog.addReferral.title")}
          </DialogTitle>
          <DialogDescription>
            {hasExistingReferral
              ? t("admin.dataTableAdminUsers.dialog.editReferral.description", {
                  name: user.fullName,
                })
              : t("admin.dataTableAdminUsers.dialog.addReferral.description", {
                  name: user.fullName,
                })}
          </DialogDescription>
        </DialogHeader>

        <Alert className="border-primary/20 bg-primary/5">
          <InfoIcon className="w-4 h-4 text-primary" />
          <AlertDescription className="text-primary">
            {t("admin.dataTableAdminUsers.dialog.addReferral.proOnlyNotice")}
          </AlertDescription>
        </Alert>

        <div className="grid gap-6 py-4">
          {/* Toggle for same code */}
          <div className="flex justify-between items-center p-3 space-x-2 rounded-lg border bg-muted/30">
            <Label htmlFor="same-code" className="flex-1 cursor-pointer">
              {t("admin.dataTableAdminUsers.dialog.addReferral.sameCodeBoth")}
            </Label>
            <Switch
              id="same-code"
              checked={useSameCode}
              onCheckedChange={setUseSameCode}
            />
          </div>

          {useSameCode ? (
            // Unified section
            renderPromoSection(
              unifiedMode,
              setUnifiedMode,
              unifiedPromoCode,
              setUnifiedPromoCode,
              unifiedDiscount,
              setUnifiedDiscount,
              unifiedDuration,
              setUnifiedDuration,
              "unified",
              t("admin.dataTableAdminUsers.dialog.addReferral.unifiedPromo")
            )
          ) : (
            // Separate sections
            <>
              {renderPromoSection(
                monthlyMode,
                setMonthlyMode,
                monthlyPromoCode,
                setMonthlyPromoCode,
                monthlyDiscount,
                setMonthlyDiscount,
                monthlyDuration,
                setMonthlyDuration,
                "monthly",
                t("admin.dataTableAdminUsers.dialog.addReferral.monthlyPromo")
              )}

              <div className="border-t" />

              {renderPromoSection(
                annualMode,
                setAnnualMode,
                annualPromoCode,
                setAnnualPromoCode,
                annualDiscount,
                setAnnualDiscount,
                annualDuration,
                setAnnualDuration,
                "annual",
                t("admin.dataTableAdminUsers.dialog.addReferral.annualPromo")
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("admin.dataTableAdminUsers.dialog.addReferral.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
            ) : hasExistingReferral ? (
              t("admin.dataTableAdminUsers.dialog.editReferral.confirm")
            ) : (
              t("admin.dataTableAdminUsers.dialog.addReferral.confirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
